import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../shared/services/api.service';
import { BehaviourSubjectService } from '../../shared/services/behaviour-subject.service';
import { AuthService } from '../../shared/services/auth.service';
import { ScrapCertificateService } from '../../shared/services/scrap-certificate.service';
import { NotificationService } from '../../shared/services/notification.service';
import { response } from 'express';
import {
  PAYMENT_FLOW,
  assignPaymentFlow,
  extractPriceBreakup,
} from '../../shared/constants/payment-flow';

@Component({
  selector: 'app-buy-now-button',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      [ngClass]="{
        'animate-cta': isBuyNowAnimating,
      }"
      [disabled]="shouldDisableButton()"
      (click)="onBuyNowClick($event)"
      [class]="
        shouldDisableButton()
          ? 'flex items-center justify-center gap-0.5 xl:gap-1 px-1 py-1.5 xl:px-2 xl:py-2 w-full min-h-8 xl:min-h-10 text-[10px] md:text-[11px] xl:text-sm font-bold text-gray-400 border border-gray-400 rounded-lg cursor-not-allowed whitespace-nowrap overflow-hidden'
          : 'flex items-center justify-center gap-0.5 xl:gap-1 px-1 py-1.5 xl:px-2 xl:py-2 w-full min-h-8 xl:min-h-10 text-[10px] md:text-[11px] xl:text-sm font-bold text-blue-500 border border-blue-500 rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap overflow-hidden'
      "
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="21"
        height="20"
        viewBox="0 0 21 20"
        fill="none"
        class="flex-shrink-0 w-3 h-3 xl:w-5 xl:h-5"
      >
        <path
          d="M6.33333 2.5H8.83333M3 5.83333H6.33333M8.83333 14.1667H5.5M8 17.5H3M12.9471 2.5L6.41697 10.5189C6.21652 10.765 6.39901 11.125 6.72426 11.125H12.1667L11.3863 17.5L17.9164 9.48112C18.1168 9.23496 17.9343 8.875 17.6091 8.875H12.1667L12.9471 2.5Z"
          [attr.stroke]="shouldDisableButton() ? '#9CA3AF' : '#307FE1'"
          stroke-width="1.25"
          stroke-cap="round"
          stroke-linejoin="round"
        />
      </svg>
      <span class="whitespace-nowrap font-bold">{{
        getButtonLabel()
      }}</span>
    </button>
  `,
  styles: [
    `
      .animate-cta {
        animation: pulse 0.5s ease-in-out;
      }

      @keyframes pulse {
        0% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.05);
        }
        100% {
          transform: scale(1);
        }
      }
    `,
  ],
})
export class BuyNowButtonComponent implements OnInit, OnDestroy {
  @Input() productData: any;
  @Input() isLocked: boolean = false;
  @Output() buyNowClicked = new EventEmitter<any>(); // Change from void to any to emit product data
  @Output() paymentSuccess = new EventEmitter<any>();

  // State management
  isBuyNowAnimating = false;
  userSelectedQuantity: any;
  isMobile: boolean = false;
  currentAction: string = 'buy_now';
  userData: any;
  currentRotute: any;
  private sub: Subscription = new Subscription();
  private destroy$ = new Subject<void>();

  constructor(
    private apiService: ApiService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private behaviourSubject: BehaviourSubjectService,
    private authService: AuthService,
    private scrapCertificateService: ScrapCertificateService,
    private notificationService: NotificationService,
    private cdref: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.currentRotute = this.router.url;
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth < 680;
      this.loadUserData();

      // Subscribe to authentication state changes
      this.authService.isLoggedIn$
        .pipe(takeUntil(this.destroy$))
        .subscribe((isLoggedIn) => {
          if (isLoggedIn) {
            // Refresh user data when user logs in
            this.loadUserData();
            this.cdref.markForCheck();
          } else {
            // Clear user data when user logs out
            this.userData = {};
            this.cdref.markForCheck();
          }
        });

      // Add window resize listener for responsive behavior
      window.addEventListener('resize', this.onResize.bind(this));
    }
  }

  onResize(): void {
    if (isPlatformBrowser(this.platformId)) {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 680;
      if (wasMobile !== this.isMobile) {
        this.cdref.markForCheck();
      }
    }
  }

  /**
   * Gets the quantity value for the buy now popup
   * Handles both kg and ton units properly
   */
  getPricebreakup() {
    // Get quantity based on unit
    let totalQuantity: number;
    if (this.productData?.part_type === 'Scrap Part') {
      if (
        this.productData?.selectedUnit === 'kg' ||
        this.productData?.selectedUnit === 'Kg'
      ) {
        totalQuantity = this.productData?.customKgQuantity
          ? parseFloat(this.productData.customKgQuantity)
          : 0;
      } else if (
        this.productData?.selectedUnit === 'liter' ||
        this.productData?.selectedUnit === 'Litr'
      ) {
        totalQuantity = this.productData?.customLiterQuantity
          ? parseFloat(this.productData.customLiterQuantity)
          : 0;
      } else {
        // For ton units
        const tonQty =
          this.productData?.quantity === 'custom'
            ? this.productData?.customTonQuantity
              ? parseFloat(this.productData.customTonQuantity)
              : 0
            : this.productData?.quantity || 0;
        totalQuantity = tonQty * 1000; // Convert tons to kg
      }
    } else {
      totalQuantity = this.productData?.quantity || 1;
    }

    const payload: Record<string, unknown> = {
      parts: [
        {
          part_number: this.productData?.part_number,
          part_type: this.productData?.part_type,
          total_quantity: totalQuantity,
        },
      ],
    };
    assignPaymentFlow(payload, PAYMENT_FLOW.BOOKING);

    this.apiService
      .getBuyNowPriceBreakup(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.success) {
            const breakup = extractPriceBreakup(res);
            if (breakup && this.productData) {
              this.productData.price_breakup = breakup;
            }
            this.buyNowClicked.emit(this.productData);
            this.cdref.detectChanges();
          } else {
            // this.notificationService.error(
            //   res.data?.msg || 'Failed to calculate price. Please try again.',
            // );
            this.cdref.markForCheck();
          }
        },
        error: (err) => {
          // this.notificationService.error(
          //   err?.error?.data?.msg ||
          //     'Failed to calculate price. Please try again.',
          // );
          this.cdref.markForCheck();
        },
      });
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.onResize.bind(this));
    }
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  private loadUserData(): void {
    const authUser = this.authService.userData;
    if (authUser?.id || authUser?.user_code) {
      this.userData = authUser;
      return;
    }

    try {
      const userDataString = localStorage.getItem('userData1');
      if (userDataString) {
        // Check if the data looks encrypted (starts with base64 pattern like "U2FsdGVkX1")
        if (userDataString.startsWith('U2FsdGVkX1')) {
          // Data is encrypted, set empty object as fallback
          this.userData = {};
          console.warn('User data appears to be encrypted');
        } else {
          this.userData = JSON.parse(userDataString);
        }
      } else {
        this.userData = {};
      }
    } catch (error: any) {
      console.error('Error loading user data:', error);
      this.userData = {};
      // this.notificationService.error(
      //   error?.error?.data?.msg ||
      //     'Failed to load user data. Please try logging in again.',
      // );
      this.cdref.markForCheck();
    }
  }

  getButtonLabel(): string {
    if (this.productData && this.productData.part_type) {
      if (this.productData.part_type === 'Scrap Part') {
        return 'Book Now';
      } else if (this.productData.part_type === 'Used Part') {
        return 'Buy Now';
      }
    }
    // Default label if part_type is not available or doesn't match expected values
    return 'Buy Now';
  }

  /**
   * Gets the selected quantity based on the product's unit selection
   */
  getSelectedQuantity(product: any): number {
    if (!product) return 0;

    if (product.part_type === 'Scrap Part') {
      if (product.selectedUnit === 'kg') {
        // For kg units, return custom kg quantity or 0 if not set
        return product.customKgQuantity
          ? parseFloat(product.customKgQuantity)
          : 0;
      } else if (product.selectedUnit === 'liter') {
        // For liter units, return custom liter quantity or 0 if not set
        if (
          !product.customLiterQuantity ||
          product.customLiterQuantity === ''
        ) {
          return 0;
        }
        const qty = parseFloat(product.customLiterQuantity);
        return isNaN(qty) ? 0 : qty;
      } else {
        // For ton units, handle predefined vs custom values
        if (product.quantity === 'custom') {
          return product.customTonQuantity
            ? parseFloat(product.customTonQuantity)
            : 0;
        }
        return typeof product.quantity === 'number' ? product.quantity : 0;
      }
    }
    // Used Part or other: at least 1
    return Math.max(product.quantity || 1, 1);
  }

  /**
   * Gets the available quantity for a product
   */
  getAvailableQuantity(product: any): number {
    if (!product) return 0;

    const stockQuantity =
      product.available_quantity !== undefined && product.available_quantity !== null
        ? product.available_quantity
        : product.fix_quantity;

    // Ensure we return at least 0, never negative or undefined
    return stockQuantity !== undefined && stockQuantity !== null
      ? Math.max(Number(stockQuantity), 0)
      : 0;
  }

  /**
   * Validates if the selected quantity is available
   */
  isQuantityAvailable(product: any): boolean {
    const selectedQuantity = this.getSelectedQuantity(product);
    const availableQuantity = this.getAvailableQuantity(product);
    const minQuantity = product.min_quantity || product.min_qty || 0;
    // For scrap parts, convert to kg for comparison
    let selectedInKg: number;
    if (product.part_type === 'Scrap Part') {
      if (product.selectedUnit === 'kg') {
        selectedInKg = selectedQuantity; // Already in kg
      } else if (product.selectedUnit === 'liter') {
        selectedInKg = selectedQuantity; // Liter is same as kg for comparison
      } else {
        selectedInKg = selectedQuantity * 1000; // Convert tons to kg
      }
    } else {
      selectedInKg = selectedQuantity; // Used parts
    }

    this.userSelectedQuantity = selectedInKg;

    // For Used Parts, if availableQuantity is not set (0), skip validation
    if (product.part_type !== 'Scrap Part' && availableQuantity === 0) {
      return selectedInKg > 0;
    }

    if (minQuantity > 0 && selectedInKg < minQuantity) {
      return false;
    }

    return selectedInKg <= availableQuantity && selectedInKg > 0;
  }

  /**
   * Checks if the button should be disabled due to quantity validation or locked status
   */
  shouldDisableButton(): boolean {
    // Only lock based on isLocked; allow clicks even if quantity is invalid
    const isLocked = this.isLocked || this.productData?.is_locked;
    return isLocked;
  }

  onBuyNowClick(event: Event): void {
    try {
      // Stop event propagation to prevent navigation and other side effects
      event.stopPropagation();
      event.preventDefault();

      // Respect locked state; otherwise allow click to trigger validation feedback
      if (this.shouldDisableButton()) {
        this.notificationService.error(
          'This part is no longer available for purchase.',
        );
        return;
      }

      // Refresh user data to ensure we have the latest authentication state
      this.loadUserData();
      this.cdref.markForCheck();

      const isLoggedIn =
        this.authService.isLoggedIn ||
        !!(this.userData?.id || this.userData?.user_code);

      // Check if user is logged in first before proceeding
      if (!isLoggedIn) {
        this.openLoginPopup('buy_now');
        return;
      }

      // Mark as touched so parent UI can show validation messages
      if (this.productData) {
        this.productData.quantityTouched = true;
      }

      // Validate quantity on click for all products against available quantity
      if (!this.isQuantityAvailable(this.productData)) {
        this.cdref.markForCheck();
        return;
      }

      localStorage.removeItem('cartbooking_page_product_data');
      localStorage.removeItem('booking_page_product_data');
      localStorage.setItem(
        'booking_page_product_data',
        JSON.stringify({
          part_number: this.productData.part_number,
          quantity: this.userSelectedQuantity,
        }),
      );
      // Check if this is a scrap part
      if (this.scrapCertificateService.isScrapPart(this.productData)) {
        // For scrap parts, check certificate match before proceeding to buy now
        this.scrapCertificateService.handleAddToCart(
          this.productData,
          () => {
            // Callback for when certificate matches - proceed with buy now
            // this.notificationService.success('Certificate verified! Proceeding with booking...');
            this.proceedWithBuyNow();
          },
          () => {
            // Callback for login required
            this.openLoginPopup('buy_now');
          },
        );
        return;
      }

      // User is logged in, proceed with buy now animation and emit event
      this.proceedWithBuyNow();
    } catch (error) {
      console.error('BuyNowButtonComponent: Error in onBuyNowClick:', error);
      // this.notificationService.error('Something went wrong. Please try again.');
      if (!this.authService.isLoggedIn) {
        this.openLoginPopup('buy_now');
      }
    }
  }

  private proceedWithBuyNow() {
    this.isBuyNowAnimating = true;
    // this.notificationService.info('Processing your request...');
    this.cdref.markForCheck();

    this.isBuyNowAnimating = false;
    this.getPricebreakup();
  }

  openLoginPopup(action: string = 'buy_now'): void {
    try {
      this.currentAction = action;

      // For both mobile and desktop users, open login modal
      this.behaviourSubject.openLoginModal('buy_now', this.productData);
    } catch (error: any) {
      console.error('Error opening login popup:', error);
      // this.notificationService.error(
      //   error?.error?.data?.msg || 'Failed to open login. Please try again.',
      // );
    }
  }

  closeLoginPopup(): void {
    try {
      // Refresh user data after login
      this.loadUserData();
      if (this.userData && this.userData.id) {
        this.notificationService.success(
          'Login successful! You can now proceed with your purchase.',
        );
      }
      this.cdref.markForCheck();
    } catch (error: any) {
      console.error('Error closing login popup:', error);
      // this.notificationService.error(
      //   error?.error?.data?.msg ||
      //     'An error occurred. Please refresh the page.',
      // );
      this.cdref.markForCheck();
    }
  }
}
